/**
 * What to do when the API answers that the session is gone.
 *
 * The transport cannot know: clearing caches and sending someone back to the
 * login screen is the app's decision, not the HTTP client's. So the client
 * reports and whoever owns the session subscribes — which is also why this
 * lives in its own module instead of inside `http/`: the session provider must
 * be able to subscribe without dragging the whole HTTP layer into the entry
 * chunk, which `bootstrap.ts` deliberately loads on demand.
 *
 * One handler, not a list: there is exactly one session in a tab, and a second
 * subscriber would mean two places deciding what "signed out" means.
 */
export type UnauthorizedHandler = () => void;

let handler: UnauthorizedHandler | undefined;

/** Registers the handler and returns the unsubscribe, ready for an effect. */
export function onUnauthorized(next: UnauthorizedHandler): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = undefined;
  };
}

/**
 * Called by the transport when a request that expected a session got a 401.
 *
 * Silent with no subscriber on purpose: the mock backend never reports one, and
 * the tests that exercise the transport do not need a session provider.
 */
export function reportUnauthorized(): void {
  handler?.();
}
